/**
 * Role claim functionality - handling role claiming/unclaiming for users
 */

import { leafClaimCache } from '../state.js';
import { refreshVisualization } from '../refreshVisualization.js';
import { apiGet, apiPost, apiDelete } from '../utils/apiClient.js';
import { csrfFetch } from '../utils/csrfFetch.js';

type ClaimStatusResponse = { claimed: boolean };

/**
 * Load claim status for current user on a role
 */
export async function loadRoleClaimStatus(roleId: number, roleName: string, loadRoleUsersCallback?: (roleId: number) => Promise<void>): Promise<void> {
    const btn = document.getElementById('claim-role-btn');
    if (!btn) return;

    try {
        console.log('[claims] Loading claim status for role:', { roleId, roleName });
        const response = await csrfFetch(`/api/roles/${roleId}/claimed`, {
            credentials: 'include'
        });

        const data = await response.json() as ClaimStatusResponse;
        console.log('[claims] Claim status loaded:', { roleId, claimed: data.claimed });
        updateClaimButton(btn as HTMLButtonElement, data.claimed, roleId, roleName, loadRoleUsersCallback);
    } catch (error) {
        console.error('Failed to load claim status:', error);
        (btn as HTMLButtonElement).textContent = 'Error loading';
        (btn as HTMLButtonElement).disabled = true;
    }
}

/**
 * Load claim status for leaf node buttons
 */
export async function loadLeafClaimStatus(roleId: string, roleName: string, starElement: SVGTextElement): Promise<void> {
    if (!starElement) return;

    // Retry logic for network errors
    let retries = 0;
    const maxRetries = 3;
    
    const attemptFetch = async (): Promise<void> => {
        try {
            const response = await csrfFetch(`/api/roles/${roleId}/claimed`, {
                credentials: 'include'
            });

            const data = await response.json() as ClaimStatusResponse;
            updateLeafClaimButton(starElement, data.claimed, roleId, roleName);
        } catch (error) {
            console.error(`Error loading claim status for role ${roleId}:`, error);
            retries++;
            if (retries < maxRetries) {
                // Retry after delay
                await new Promise(resolve => setTimeout(resolve, 500 * retries));
                await attemptFetch();
            } else {
                console.warn(`Failed to load claim status for role ${roleId} after ${maxRetries} attempts`);
                // Don't update star if we can't fetch - it will show unclaimed by default
            }
        }
    };
    
    await attemptFetch();
}

/**
 * Update claim button UI state
 */
export function updateClaimButton(btn: HTMLButtonElement, isClaimed: boolean, roleId: number, roleName: string, loadRoleUsersCallback?: (roleId: number) => Promise<void>): void {
    const iconSpan = btn.querySelector('.btn-icon');
    const textSpan = btn.querySelector('.btn-text');

    btn.disabled = false;
    btn.className = isClaimed ? 'claim-role-btn claimed' : 'claim-role-btn';

    if (iconSpan) iconSpan.textContent = isClaimed ? '★' : '☆';
    if (textSpan) textSpan.textContent = isClaimed ? 'This is my role' : 'Claim this role';

    btn.onclick = async () => {
        await toggleRoleClaim(roleId, roleName, !isClaimed, loadRoleUsersCallback);
    };
}

/**
 * Update leaf node claim button/star UI state
 */
export function updateLeafClaimButton(starElement: SVGTextElement, isClaimed: boolean, roleId: string, roleName: string): void {
    // Cache the claim status so it persists across label recreation
    leafClaimCache[roleId] = isClaimed;
    
    if (isClaimed) {
        // If claiming and star doesn't exist, create it
        if (!starElement) {
            // Star will be created on next label refresh
            if (typeof (window as any).addCircleLabels === 'function') {
                (window as any).addCircleLabels();
            }
        } else {
            // Update existing star
            while (starElement.firstChild) {
                starElement.removeChild(starElement.firstChild);
            }
            starElement.appendChild(document.createTextNode('★'));
            starElement.classList.remove('unclaimed');
            starElement.classList.add('claimed');
            starElement.setAttribute('title', `This is my role: ${roleName}`);
        }
    } else {
        // If unclaiming, remove the star
        if (starElement && starElement.parentNode) {
            starElement.parentNode.removeChild(starElement);
        }
    }
}

/**
 * Toggle role claim for current user
 */
export async function toggleRoleClaim(roleId: number, roleName: string, claim: boolean, loadRoleUsersCallback?: (roleId: number) => Promise<void>): Promise<void> {
    const btn = document.getElementById('claim-role-btn') as HTMLButtonElement;
    if (!btn) return;

    btn.disabled = true;
    const textSpan = btn.querySelector('.btn-text');
    if (textSpan) textSpan.textContent = claim ? 'Claiming...' : 'Unclaiming...';

    try {
        console.log('[claims] Toggling role claim:', { roleId, roleName, claim });
        if (claim) {
            const response = await csrfFetch(`/api/roles/${roleId}/claim`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({}),
                credentials: 'include'
            });
            console.log('[claims] Role claimed successfully:', { roleId });

            if (!response.ok) {
                throw new Error(`Failed to claim role: ${response.status}`);
            }
        } else {
            console.log('[claims] Unclaiming role:', { roleId });
            const response = await csrfFetch(`/api/roles/${roleId}/claim`, {
                method: 'DELETE',
                credentials: 'include'
            });
            console.log('[claims] Role unclaimed successfully:', { roleId });

            if (!response.ok) {
                throw new Error(`Failed to unclaim role: ${response.status}`);
            }
        }

        updateClaimButton(btn, claim, roleId, roleName, loadRoleUsersCallback);
        
        // Update local cache immediately
        leafClaimCache[String(roleId)] = claim;
        
        // Refresh the users list if callback provided
        if (loadRoleUsersCallback) {
            loadRoleUsersCallback(roleId);
        }
        
        // Refresh visualization to update stars
        await refreshVisualization();
        
        // Invalidate cache before reapplying highlights
        if (typeof (window as any).invalidateClaimedRolesCache === 'function') {
            (window as any).invalidateClaimedRolesCache();
        }
        
        // Reapply my roles highlight if active
        if (typeof (window as any).reapplyMyRolesHighlight === 'function') {
            (window as any).reapplyMyRolesHighlight();
        }
        
        // Update my roles overview if visible
        if (typeof (window as any).updateMyRolesOverview === 'function') {
            (window as any).updateMyRolesOverview();
        }
    } catch (error) {
        console.error('Failed to toggle claim:', error);
        if (textSpan) textSpan.textContent = 'Error';
        setTimeout(() => {
            updateClaimButton(btn, !claim, roleId, roleName, loadRoleUsersCallback);
        }, 2000);
    }
}

/**
 * Toggle leaf node claim
 */
export async function toggleLeafClaim(roleId: string, roleName: string, claim: boolean, starElement?: SVGTextElement, loadRoleUsersCallback?: (roleId: number) => Promise<void>): Promise<void> {
    // Find the current star element by roleId (may not exist if unclaimed)
    const currentStar = document.querySelector(`text.label__claim-star[data-role-id="${roleId}"]`) as SVGTextElement;

    // Store original state for restoration if needed
    const originalIsClaimed = leafClaimCache[roleId] ?? false;
    
    // Optimistic update - immediately show new state
    updateLeafClaimButton(currentStar, claim, roleId, roleName);

    try {
        if (claim) {
            const response = await csrfFetch(`/api/roles/${roleId}/claim`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({}),
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Failed to claim role: ${response.status}`);
            }
        } else {
            const response = await csrfFetch(`/api/roles/${roleId}/claim`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Failed to unclaim role: ${response.status}`);
            }
        }
        
        // Reload users list if panel is showing this role
        const panelContent = document.querySelector('.panel-content');
        if (panelContent && (panelContent as HTMLElement).dataset.currentNode === roleName && loadRoleUsersCallback) {
            loadRoleUsersCallback(Number(roleId));
        }
        
        // Refresh visualization to update stars
        await refreshVisualization();
        
        // Invalidate cache before reapplying highlights
        if (typeof (window as any).invalidateClaimedRolesCache === 'function') {
            (window as any).invalidateClaimedRolesCache();
        }
        
        // Reapply my roles highlight if active
        if (typeof (window as any).reapplyMyRolesHighlight === 'function') {
            (window as any).reapplyMyRolesHighlight();
        }
        
        // Update my roles overview if visible
        if (typeof (window as any).updateMyRolesOverview === 'function') {
            (window as any).updateMyRolesOverview();
        }
    } catch (error) {
        console.error('Failed to toggle claim:', error);
        // Revert to original state on error
        updateLeafClaimButton(currentStar, originalIsClaimed, roleId, roleName);
    }
}
